/**
 * This file is used to lazy load our "root" components.
 * Anything imported in this file should have a corresponding
 * Rails view that renders it.
 *
 * The `REGISTRY` keys map to the component names passed to the
 * `render_react_component` helper in the Rails views.
 *
 * When adding new components to the registry, make sure to use
 * the `lazyNamedImport` function for named exports and `lazyDefaultImport`
 * for default exports.
 */

import { lazy } from 'react';

// Import helpers
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lazyDefaultImport = (importFn: () => Promise<any>) => lazy(importFn);

// React.lazy only supports default exports, so we need to use a workaround for named exports
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lazyNamedImport = (importFn: () => Promise<any>, name: string) =>
  lazy(() => importFn().then((module) => ({ default: module[name] })));

export const REGISTRY = {
  AcademyCrossSell: lazyNamedImport(
    () => import('./features/cross-sells/components/AcademyCrossSell'),
    'AcademyCrossSell',
  ),
  AcademyCertificationsV2Router: lazyNamedImport(
    () => import('./clc/features/academy-certifications-v2'),
    'AcademyCertificationsV2Router',
  ),
  AcademyGradebookRouter: lazyNamedImport(
    () => import('./clc/features/academy-gradebook/routes/AcademyGradebookRouter'),
    'AcademyGradebookRouter',
  ),
  AgentsRouter: lazyNamedImport(() => import('./elc/features/agents'), 'AgentsRouter'),
  AISimulationsContentRouter: lazyNamedImport(
    () => import('./elc/features/ai-simulations'),
    'AISimulationsContentRouter',
  ),
  AiAcademySettings: lazyNamedImport(
    () => import('./clc/features/academies/admin/components/AiAcademySettings'),
    'AiAcademySettings',
  ),
  EmailAcademySettings: lazyNamedImport(
    () => import('./clc/features/academy-email-settings/components/EmailAcademySettings'),
    'EmailAcademySettings',
  ),
  GeneralAcademySettings: lazyNamedImport(
    () => import('./clc/features/academies/settings/admin/components/GeneralAcademySettings'),
    'GeneralAcademySettings',
  ),
  AcademyObjectAttributeSettingsPage: lazyNamedImport(
    () => import('./clc/features/academy-object-attributes/routes/AcademyObjectAttributeSettingsPage'),
    'AcademyObjectAttributeSettingsPage',
  ),
  AcademyRecommendationSettingsPage: lazyNamedImport(
    () => import('./clc/features/academy-recommendations'),
    'AcademyRecommendationSettingsPage',
  ),
  CertificationAcademySettings: lazyNamedImport(
    () => import('./clc/features/academies/settings/admin/components/CertificationAcademySettings'),
    'CertificationAcademySettings',
  ),
  AcademyLearnerCertifications: lazyNamedImport(
    () => import('./components/academies/learner/AcademyLearnerCertifications'),
    'AcademyLearnerCertifications',
  ),
  DedicatedLearnerSearchPage: lazyNamedImport(
    () => import('./clc/features/clc-hub-learner-search'),
    'DedicatedLearnerSearchPage',
  ),
  AdminProfileRoute: lazyNamedImport(() => import('./elc/features/manager-dashboard'), 'AdminProfileRoute'),
  AdminSettingsUserList: lazyNamedImport(() => import('./elc/features/admin-settings'), 'AdminSettingsUserList'),
  AdvancedSearchPage: lazyNamedImport(
    () => import('./elc/features/learner-search/components/AdvancedSearchPage'),
    'AdvancedSearchPage',
  ),
  ChallengeOverviewRouter: lazyNamedImport(
    () => import('./elc/features/admin-content-overview'),
    'ChallengeOverviewRouter',
  ),
  ChallengeReviewerHome: lazyNamedImport(
    () => import('./elc/features/challenge-reviewer-dashboard'),
    'ChallengeReviewerHome',
  ),
  CLCProfilePage: lazyNamedImport(
    () => import('./clc/features/customer-learning-cloud-hub/routes/learner/CLCProfilePage'),
    'CLCProfilePage',
  ),
  CMSFileViewer: lazyNamedImport(() => import('./elc/features/cms/components/CMSFileViewer'), 'CMSFileViewer'),
  CMSExternalPreviewPage: lazyNamedImport(
    () => import('./elc/features/cms/components/CMSExternalPreviewPage'),
    'CMSExternalPreviewPage',
  ),
  CMSRouter: lazyNamedImport(() => import('./elc/features/cms/routes/CMSRouter'), 'CMSRouter'),
  CommunityPreview: lazyNamedImport(
    () => import('./clc/features/theming/admin/components/Previews/CommunityPreview'),
    'CommunityPreview',
  ),
  ContentProviderDevelopmentRouter: lazyNamedImport(
    () => import('./features/learner-content-provider-development'),
    'ContentProviderDevelopmentRouter',
  ),
  CustomerCloudForgotPassword: lazyNamedImport(
    () => import('./clc/features/customer-learning-cloud-hub/features/login-and-registration'),
    'CustomerCloudForgotPassword',
  ),
  CustomerCloudLogin: lazyNamedImport(
    () => import('./clc/features/customer-learning-cloud-hub/features/login-and-registration'),
    'CustomerCloudLogin',
  ),
  CustomerCloudRegister: lazyNamedImport(
    () => import('./clc/features/customer-learning-cloud-hub/features/login-and-registration'),
    'CustomerCloudRegister',
  ),
  CustomerCloudResetPassword: lazyNamedImport(
    () => import('./clc/features/customer-learning-cloud-hub/features/login-and-registration'),
    'CustomerCloudResetPassword',
  ),
  CustomerLearningCloudHubRouter: lazyNamedImport(
    () =>
      import('./clc/features/customer-learning-cloud-hub/routes/CustomerLearningCloudHubRouter/CustomerLearningCloudHubRouter'),
    'CustomerLearningCloudHubRouter',
  ),
  CustomerCloudNoAccess: lazyNamedImport(
    () => import('./clc/features/customer-learning-cloud-hub/features/login-and-registration'),
    'CustomerCloudNoAccess',
  ),
  DefaultAcademyErrorPage: lazyNamedImport(
    () => import('./components/academies/common/DefaultAcademyErrorPage'),
    'DefaultAcademyErrorPage',
  ),
  DevelopmentContentRouter: lazyNamedImport(
    () => import('./elc/features/learner-development'),
    'DevelopmentContentRouter',
  ),
  GuideOverviewRouter: lazyNamedImport(() => import('./elc/features/admin-content-overview'), 'GuideOverviewRouter'),
  HiddenTraining: lazyNamedImport(() => import('./features/common/components/HiddenTraining'), 'HiddenTraining'),
  ELCIntegrationsRouter: lazyNamedImport(
    () => import('./elc/features/integrations/routes/ELCIntegrationsRouter'),
    'ELCIntegrationsRouter',
  ),
  CLCIntegrationsRouter: lazyNamedImport(() => import('./clc/features/academy-integrations'), 'CLCIntegrationsRouter'),
  LearnerCommunityRouter: lazyNamedImport(
    () => import('./clc/features/community/learner/routes/LearnerCommunityRouter'),
    'LearnerCommunityRouter',
  ),
  LearnerEventsHome: lazyNamedImport(() => import('./elc/features/learner-events'), 'LearnerEventsHome'),
  LearnerCompanyRouter: lazyNamedImport(() => import('./elc/features/learner'), 'LearnerCompanyRouter'),
  LearnerHome: lazyNamedImport(() => import('./elc/features/learner'), 'LearnerHome'),
  LearnerProfile: lazyNamedImport(() => import('./elc/features/learner'), 'LearnerProfile'),
  LearnerSavedContent: lazyNamedImport(() => import('./elc/features/learner-saved-content'), 'LearnerSavedContent'),
  LearnerSettings: lazyNamedImport(() => import('./elc/features/learner'), 'LearnerSettings'),
  LibraryRouter: lazyNamedImport(() => import('./elc/features/library'), 'LibraryRouter'),
  LoginWithUpsell: lazyNamedImport(() => import('./components/LoginWithUpsell'), 'LoginWithUpsell'),
  ManagerDashboardRouter: lazyNamedImport(() => import('./elc/features/manager-dashboard'), 'ManagerDashboardRouter'),
  PathOverviewRouter: lazyNamedImport(() => import('./elc/features/admin-content-overview'), 'PathOverviewRouter'),
  ReportingRouter: lazyNamedImport(() => import('./elc/features/reporting'), 'ReportingRouter'),
  SavedReports: lazyNamedImport(
    () => import('./clc/features/academy-training-analytics/routes/SavedReports'),
    'SavedReports',
  ),
  ScormHistoryAcademyRouter: lazyNamedImport(
    () => import('./features/scorm-assignment-history/routes/ScormHistoryAcademyRouter'),
    'ScormHistoryAcademyRouter',
  ),
  ScormHistoryElcRouter: lazyNamedImport(
    () => import('./features/scorm-assignment-history/routes/ScormHistoryElcRouter'),
    'ScormHistoryElcRouter',
  ),
  SkillsRouter: lazyNamedImport(() => import('./elc/features/skills'), 'SkillsRouter'),
  TaskAssignmentPreview: lazyNamedImport(() => import('./elc/features/admin-guides'), 'TaskAssignmentPreview'),
  TrainingAnalyticsPage: lazyNamedImport(
    () => import('./clc/features/academy-training-analytics/routes/TrainingAnalyticsPage'),
    'TrainingAnalyticsPage',
  ),
  UserConsentModal: lazyNamedImport(() => import('./components/common/UserConsentModal'), 'UserConsentModal'),
  VersionHistoryPage: lazyNamedImport(() => import('./elc/features/admin-guides/routes'), 'VersionHistoryPage'),
  AcademyAdminAnalyticsV2: lazyDefaultImport(() => import('./components/academies/admin/academy-admin-analytics-v2')),
  AcademyAdminCatalogSettingsPage: lazyNamedImport(
    () => import('./clc/features/academy-object-attributes/routes/AcademyAdminCatalogSettingsPage'),
    'AcademyAdminCatalogSettingsPage',
  ),
  AcademyAdminCertification: lazyDefaultImport(
    () => import('./components/academies/admin/academy-admin-certification'),
  ),
  AwardedAcademyCertificationPreview: lazyNamedImport(
    () => import('./clc/features/academy-certifications-v2'),
    'AwardedAcademyCertificationPreview',
  ),
  AcademyAdminAllCertificationsEnrollmentOverview: lazyNamedImport(
    () => import('./clc/features/academy-enrollments'),
    'AcademyAdminAllCertificationsEnrollmentOverview',
  ),
  AcademyAdminAllPathsEnrollmentOverview: lazyNamedImport(
    () => import('./clc/features/academy-enrollments'),
    'AcademyAdminAllPathsEnrollmentOverview',
  ),
  AcademyAdminAllTrainingsEnrollmentOverview: lazyNamedImport(
    () => import('./clc/features/academy-enrollments'),
    'AcademyAdminAllTrainingsEnrollmentOverview',
  ),
  AcademyAdminCertifications: lazyDefaultImport(
    () => import('./components/academies/admin/academy-admin-certifications'),
  ),
  AcademyAdminCollectionContent: lazyDefaultImport(
    () => import('./components/academies/admin/academy-admin-collection-content'),
  ),
  AcademyAdminContent: lazyDefaultImport(() => import('./components/academies/admin/academy-admin-content')),
  AcademyAdminContentSettings: lazyDefaultImport(
    () => import('./components/academies/admin/academy-admin-content-settings'),
  ),
  Content: lazyDefaultImport(() => import('./clc/features/academies/content/Content')),

  AcademyAdminDataViz: lazyDefaultImport(() => import('./components/academies/admin/academy-admin-data-viz')),
  AcademyAdminEcommercePromos: lazyDefaultImport(
    () => import('./clc/features/academy-ecommerce/components/academy-admin-ecommerce-promos'),
  ),
  AcademyAdminEcommercePurchases: lazyDefaultImport(
    () => import('./clc/features/academy-ecommerce/components/academy-admin-ecommerce-purchases'),
  ),
  EcommerceAcademySettings: lazyNamedImport(
    () => import('./clc/features/academies/settings/admin/components/EcommerceAcademySettings'),
    'EcommerceAcademySettings',
  ),
  AcademyAdminEmailPreview: lazyNamedImport(
    () => import('./clc/features/academy-email-settings/components/AcademyAdminEmailPreview'),
    'AcademyAdminEmailPreview',
  ),
  AcademyAdminPreview: lazyDefaultImport(() => import('./components/academies/admin/academy-admin-preview')),
  AcademyAdminTheming: lazyNamedImport(() => import('./clc/features/academy-theming'), 'AcademyAdminTheming'),
  AcademyAdminTextOverrides: lazyNamedImport(
    () => import('./components/academies/admin/AcademyAdminTextOverrides'),
    'AcademyAdminTextOverrides',
  ),
  AcademyAdminTrainings: lazyDefaultImport(() => import('./components/academies/admin/academy-admin-trainings')),
  AcademyAdminUserAnalytics: lazyDefaultImport(
    () => import('./components/academies/admin/academy-admin-user-analytics'),
  ),
  AcademyAdminUsers: lazyDefaultImport(() => import('./components/academies/admin/academy-admin-users')),
  AcademyAdminPages: lazyDefaultImport(() => import('./clc/features/academies/pages/AcademyAdminPages')),
  AcademyCustomPage: lazyDefaultImport(() => import('./clc/features/academies/pages/AcademyCustomPage')),
  AcademyCheckout: lazyDefaultImport(() => import('./components/academies/learner/academy-checkout')),
  AcademyEventsV2Router: lazyNamedImport(() => import('./clc/features/academy-events-v2'), 'AcademyEventsV2Router'),
  AcademyLearnerCertification: lazyDefaultImport(
    () => import('./components/academies/learner/academy-learner-certification'),
  ),
  AcademyLearnerCatalogPage: lazyNamedImport(
    () => import('./clc/features/academy-object-attributes/routes/AcademyLearnerCatalogPage'),
    'AcademyLearnerCatalogPage',
  ),
  AcademyLearnerCollection: lazyDefaultImport(
    () => import('./components/academies/learner/academy-learner-collection'),
  ),
  AcademyLearnerContentOverview: lazyDefaultImport(
    () => import('./components/academies/learner/academy-learner-content-overview'),
  ),
  AcademyLearnerCustomPage: lazyNamedImport(() => import('./features/learner-pages'), 'AcademyLearnerCustomPage'),
  AcademyLearnerEvent: lazyDefaultImport(() => import('./components/academies/learner/academy-learner-event')),
  AcademyLearnerEventOnDemandContent: lazyDefaultImport(
    () => import('./components/academies/learner/academy-learner-event-on-demand-content'),
  ),
  AcademyLearnerEvents: lazyDefaultImport(() => import('./components/academies/learner/academy-learner-events')),
  AcademyLearnerForgotPassword: lazyDefaultImport(
    () => import('./components/academies/learner/academy-learner-forgot-password'),
  ),
  AcademyLearnerHome: lazyDefaultImport(() => import('./components/academies/learner/academy-learner-home')),
  AcademyLearnerInvitationDeleted: lazyDefaultImport(
    () => import('./components/academies/learner/academy-learner-invitation-deleted'),
  ),
  AcademyLearnerLogin: lazyDefaultImport(() => import('./components/academies/learner/academy-learner-login')),
  AcademyLearnerLoginAcceptTerms: lazyDefaultImport(
    () => import('./components/academies/learner/academy-learner-login-accept-terms'),
  ),
  AcademyLearnerMyOrders: lazyDefaultImport(
    () => import('./components/academies/learner/orders/academy-learner-my-orders'),
  ),
  AcademyLearnerMyTrainings: lazyDefaultImport(
    () => import('./components/academies/learner/my-trainings/my-trainings'),
  ),
  AcademyLearnerProfileMyTrainings: lazyDefaultImport(
    () => import('./components/academies/learner/academy-learner-profile-my-trainings'),
  ),
  AcademyLearnerResetPassword: lazyDefaultImport(
    () => import('./components/academies/learner/academy-learner-reset-password'),
  ),
  AcademyLearnerSSOFailure: lazyDefaultImport(
    () => import('./components/academies/learner/academy-learner-sso-failure'),
  ),
  AdminContentEditor: lazyDefaultImport(() => import('./components/admin-content/admin-content-editor')),
  AdminInternalDataViz: lazyDefaultImport(() => import('./components/admin-data-viz/admin-internal-data-viz')),
  AdminTools: lazyNamedImport(() => import('./elc/features/admin-tools'), 'AdminTools'),
  AdminTrainingClass: lazyDefaultImport(() => import('./components/training_classes/v2/admin-training-class')),
  AdminTrainingHome: lazyDefaultImport(() => import('./components/admin-guide-viewer/admin-training-home')),
  AdminTrainingWorkflowViewer: lazyDefaultImport(
    () => import('./components/admin-training-workflow-viewer/admin-training-workflow-viewer'),
  ),
  AdminTrashRouter: lazyNamedImport(() => import('./elc/features/admin-trash'), 'AdminTrashRouter'),
  AdminSearchRouter: lazyNamedImport(() => import('./elc/features/admin-search'), 'AdminSearchRouter'),
  AdvancedSearchLanding: lazyDefaultImport(() => import('./components/advanced-search/advanced-search-landing')),
  AdvancedSearchEmbed: lazyNamedImport(
    () => import('./elc/features/learner-search/components/AdvancedSearchEmbed'),
    'AdvancedSearchEmbed',
  ),
  AllCertificationsEditor: lazyDefaultImport(
    () => import('./components/enterprise-setup/all-certifications-editor/all-certifications-editor'),
  ),
  AllGroupsEditor: lazyDefaultImport(() => import('./components/enterprise-setup/all-groups-editor/all-groups-editor')),
  AllObjectTagsEditor: lazyDefaultImport(() => import('./components/object-tags/all-object-tags-editor')),
  AllRolesEditor: lazyDefaultImport(() => import('./components/enterprise-setup/roles/all-roles-editor')),
  AssignmentDownloader: lazyDefaultImport(() => import('./components/guide-previewer/assignment-downloader')),
  AssetManagerRouter: lazyNamedImport(
    () => import('./elc/features/asset-manager/routes/AssetManagerRouter/AssetManagerRouter'),
    'AssetManagerRouter',
  ),
  AwardedAcademyCertificationLanding: lazyDefaultImport(
    () => import('./components/academies/common/awarded-academy-certification-landing'),
  ),
  BambooCallback: lazyDefaultImport(() => import('./components/enterprise-setup/integrations-editor/BambooCallback')),
  ContentActions: lazyNamedImport(() => import('./elc/features/content-action'), 'ContentActions'),
  CertificationPage: lazyDefaultImport(() => import('./components/academies/admin/CertificationPage')),
  CertificationPreviewer: lazyDefaultImport(
    () => import('./components/enterprise-setup/certification-previewer/certification-previewer'),
  ),
  CertificationsIndex: lazyDefaultImport(
    () => import('./clc/features/academies/content-index-pages/CertificationsIndex'),
  ),
  CollectionsIndex: lazyDefaultImport(() => import('./clc/features/academies/content-index-pages/CollectionsIndex')),
  EventsIndex: lazyDefaultImport(() => import('./clc/features/academies/content-index-pages/EventsIndex')),
  ChallengeAdminReview: lazyDefaultImport(() => import('./components/admin-challenge-viewer/challenge-admin-review')),
  ChallengeEditor: lazyDefaultImport(() => import('./components/challenges/challenge-editor')),
  ChallengeEndUserView: lazyDefaultImport(() => import('./components/challenges/challenge-end-user-view')),
  ChallengePreviewer: lazyDefaultImport(() => import('./components/challenges/challenge-previewer')),
  ChallengeReviewerAssignment: lazyDefaultImport(() => import('./components/challenges/challenge-reviewer-assignment')),
  CLCEmbeddedAnalyticsRoute: lazyNamedImport(
    () => import('./clc/features/academy-embedded-analytics'),
    'CLCEmbeddedAnalyticsRoute',
  ),
  CLCInstantLoginBanner: lazyNamedImport(() => import('./clc/components/instant-login-banner'), 'InstantLoginBanner'),
  Document: lazyDefaultImport(() => import('./components/document/document')),
  DownloadPdf: lazyDefaultImport(() => import('./components/guide-previewer/download-pdf')),
  DynamicGroupsEditor: lazyDefaultImport(() => import('./components/dynamic-groups/dynamic-groups-editor')),
  DynamicGroupsEditorInternal: lazyDefaultImport(
    () => import('./components/dynamic-groups/dynamic-groups-editor-internal'),
  ),
  EmailSetup: lazyDefaultImport(() => import('./components/enterprise-setup/email-setup')),
  EmbedContent: lazyDefaultImport(() => import('./components/embed/embed-content')),
  EmbedHeader: lazyDefaultImport(() => import('./components/common/embed-header')),
  EmbedSearch: lazyDefaultImport(() => import('./components/embed/embed-search')),
  ELCEmbeddedAnalyticsRoute: lazyNamedImport(
    () => import('./elc/features/embedded-analytics'),
    'ELCEmbeddedAnalyticsRoute',
  ),
  EnterpriseSettingsRouter: lazyNamedImport(
    () => import('./elc/features/enterprise-settings/routes/EnterpriseSettingsRouter'),
    'EnterpriseSettingsRouter',
  ),
  ExternalTraining: lazyDefaultImport(() => import('./guide/external_training')),
  ExternalTrainingAdminReview: lazyDefaultImport(() => import('./components/academies/admin/external_training_review')),
  ForgotPassword: lazyNamedImport(() => import('./components/Login/ForgotPassword'), 'ForgotPassword'),
  Go1Router: lazyNamedImport(() => import('./elc/features/go1/routes/Go1Router'), 'Go1Router'),
  GuideAssignmentReminder: lazyDefaultImport(
    () => import('./components/guide_assignment_reminder/guide_assignment_reminder'),
  ),
  GuideAssignmentReviewer: lazyDefaultImport(
    () => import('./components/guide-assignment-reviewer/guide-assignment-reviewer'),
  ),
  GuideAssignmentSubmissions: lazyDefaultImport(
    () => import('./components/guide-assignment-submissions/guide-assignment-submissions'),
  ),
  GuideAssignmentViewer: lazyDefaultImport(
    () => import('./components/guide-assignment-viewer/guide-assignment-viewer'),
  ),
  GuideEditor: lazyDefaultImport(() => import('./components/guide-editor/guide-editor')),
  GuidePreviewer: lazyDefaultImport(() => import('./components/guide-previewer/guide-previewer')),
  GuideStatsPage: lazyDefaultImport(() => import('./components/guide-assignment-submissions/guide-stats-page')),
  AllSubmissionsView: lazyNamedImport(() => import('./elc/features/guide-submissions'), 'AllSubmissionsView'),
  HelpViewer: lazyDefaultImport(() => import('./components/help-viewer/help-viewer')),
  HorizontalNavbar: lazyDefaultImport(() => import('./components/navigation/horizontal-navbar')),
  InstantLoginBanner: lazyDefaultImport(() => import('./components/common/instant-login-banner')),
  ItemFolderPane: lazyDefaultImport(() => import('./components/common/item-folder-pane/item-folder-pane')),
  Login: lazyDefaultImport(() => import('./components/login-legacy/login_legacy')),
  McpAuthorizationConsent: lazyNamedImport(
    () => import('./features/oauth-doorkeeper/components/McpAuthorizationConsent'),
    'McpAuthorizationConsent',
  ),
  Notification: lazyDefaultImport(() => import('./components/notification/notification')),
  OAuthAuthorizationScopes: lazyNamedImport(
    () => import('./features/oauth-doorkeeper/components/OAuthAuthorizationScopes'),
    'OAuthAuthorizationScopes',
  ),
  PathViewRouter: lazyNamedImport(
    () => import('./elc/features/content-view-path/routes/PathViewRouter'),
    'PathViewRouter',
  ),
  Register: lazyNamedImport(() => import('./components/Register/Register'), 'Register'),
  RegisterStep2: lazyDefaultImport(() => import('./components/register-legacy/register-step2')),
  ReportBuilder: lazyDefaultImport(() => import('./components/reporting/builder/report-builder')),
  ReportingAllCertifications: lazyDefaultImport(
    () => import('./components/reporting-all-certifications/reporting-all-certifications'),
  ),
  ReportingAllGroups: lazyDefaultImport(() => import('./components/reporting-all-groups/reporting-all-groups')),
  ReportingAllGuides: lazyDefaultImport(() => import('./components/reporting-all-guides/reporting-all-guides')),
  ReportingAllUsers: lazyDefaultImport(() => import('./components/reporting-all-users/reporting-all-users')),
  ReportingCustom: lazyDefaultImport(() => import('./components/reporting-custom/reporting-custom')),
  ReportingSingleCertification: lazyDefaultImport(
    () => import('./components/reporting-single-certification/reporting-single-certification'),
  ),
  ReportingSingleGroupGuides: lazyDefaultImport(
    () => import('./components/reporting-single-group/reporting-single-group-guides'),
  ),
  ReportingSingleGroupUsers: lazyDefaultImport(
    () => import('./components/reporting-single-group/reporting-single-group-users'),
  ),
  ResetPassword: lazyNamedImport(() => import('./components/Login/ResetPassword'), 'ResetPassword'),
  ResourceViewerFromEditor: lazyDefaultImport(
    () => import('./components/universities/resources/resource_viewer_from_editor'),
  ),
  ResourceViewerFromSeriesAssignment: lazyDefaultImport(
    () => import('./components/universities/resources/resource_viewer_from_series_assignment'),
  ),
  ResourceViewerFromSeriesEditorPreview: lazyDefaultImport(
    () => import('./components/universities/resources/resource_viewer_from_series_editor_preview'),
  ),
  ResourceViewerFromUniversity: lazyDefaultImport(
    () => import('./components/universities/resources/resource_viewer_from_university'),
  ),
  ResourceViewerFromUniversitySeriesPreview: lazyDefaultImport(
    () => import('./components/universities/resources/resource_viewer_from_university_series_preview'),
  ),
  RoleEditor: lazyDefaultImport(() => import('./components/enterprise-setup/roles/role-editor')),
  SAMLEnterprisePicker: lazyDefaultImport(() => import('./components/login-legacy/saml_enterprise_picker')),
  ScormSubmissions: lazyDefaultImport(() => import('./components/guide-submissions/scorm-submissions')),
  ShareLink: lazyDefaultImport(() => import('./components/share_link/share_link')),
  SingleGroupEditor: lazyDefaultImport(
    () => import('./components/enterprise-setup/all-groups-editor/single-group-editor'),
  ),
  Task: lazyDefaultImport(() => import('./components/task/task')),
  TaskAssignmentReviewer: lazyDefaultImport(
    () => import('./components/task_assignment_reviewer/task_assignment_reviewer'),
  ),
  TaskEditor: lazyDefaultImport(() => import('./components/task_editor/task_editor')),
  TaskPreviewer: lazyDefaultImport(() => import('./components/task_previewer/task_previewer')),
  TaskViewer: lazyDefaultImport(() => import('./components/task_viewer/task_viewer')),
  TrainingClassUserViewV2: lazyDefaultImport(
    () => import('./components/training_classes/v2/TrainingClassUserViewV2/TrainingClassUserViewV2'),
  ),
  TrainingPage: lazyDefaultImport(() => import('./components/academies/admin/TrainingPage')),
  TrainingSeriesEditor: lazyDefaultImport(() => import('./components/training-series-editor/training-series-editor')),
  TrainingWorkflowEditor: lazyDefaultImport(
    () => import('./components/training-workflow-editor/training-workflow-editor'),
  ),
  TrainingWorkflowPreviewer: lazyDefaultImport(
    () => import('./components/training-workflow-previewer/training-workflow-previewer'),
  ),
  University: lazyDefaultImport(() => import('./components/universities/university')),
  UniversityMembers: lazyDefaultImport(() => import('./components/universities/members/university-members')),
  UniversityResourceEditor: lazyDefaultImport(
    () => import('./components/universities/resources/university_resource_editor'),
  ),
  UniversityResourceViewer: lazyDefaultImport(
    () => import('./components/universities/resources/university_resource_viewer'),
  ),
  UserInvitationRegister: lazyNamedImport(
    () => import('./components/Register/UserInvitationRegister'),
    'UserInvitationRegister',
  ),
  WorkflowsRouter: lazyNamedImport(
    () => import('./elc/features/workflows/routes/WorkflowsRouter/WorkflowsRouter'),
    'WorkflowsRouter',
  ),

  // Spacemountain
  SpaceMountainAccessRequestsIndex: lazyDefaultImport(
    () => import('./components/space-mountain/space-mountain-access-requests-index'),
  ),
  SpaceMountainAssetLibrary: lazyDefaultImport(
    () => import('./components/space-mountain/space-mountain-asset-library'),
  ),
  SpaceMountainCopyFolders: lazyDefaultImport(() => import('./components/space-mountain/space-mountain-copy-folders')),
  SpaceMountainEmailTools: lazyDefaultImport(
    () => import('./components/space-mountain/email-tools/space-mountain-email-tools'),
  ),
  SpaceMountainEnterpriseCertificationTemplates: lazyDefaultImport(
    () => import('./components/space-mountain/space-mountain-enterprise-certification-templates'),
  ),
  SpaceMountainEnterpriseEdit: lazyDefaultImport(
    () => import('./components/space-mountain/space-mountain-enterprise-edit'),
  ),
  SpaceMountainEnterpriseGo1Integration: lazyDefaultImport(
    () => import('./components/space-mountain/space-mountain-enterprise-go1-integration'),
  ),
  SpaceMountainEnterpriseAISimulationsIntegration: lazyDefaultImport(
    () => import('./components/space-mountain/space-mountain-enterprise-ai-simulations-integration'),
  ),
  SpaceMountainEnterpriseMetrics: lazyDefaultImport(
    () => import('./components/space-mountain/space-mountain-enterprise-metrics'),
  ),
  SpaceMountainEnterpriseNew: lazyDefaultImport(
    () => import('./components/space-mountain/space-mountain-enterprise-new'),
  ),
  SpaceMountainGo1OnboardingForm: lazyDefaultImport(
    () => import('./components/space-mountain/space-mountain-go1-onboarding-form'),
  ),
  SpaceMountainImportableContentEditor: lazyDefaultImport(
    () => import('./components/space-mountain/space-mountain-importable-content-editor'),
  ),
  SpaceMountainImportItems: lazyDefaultImport(() => import('./components/space-mountain/space-mountain-import-items')),
  SpaceMountainIndex: lazyDefaultImport(() => import('./components/space-mountain/space-mountain-index')),
  SpaceMountainMetrics: lazyDefaultImport(() => import('./components/space-mountain/space-mountain-metrics')),
  SpaceMountainPayingAdminEmails: lazyDefaultImport(
    () => import('./components/space-mountain/space-mountain-paying-admin-emails'),
  ),
  SpaceMountainSsoEditor: lazyDefaultImport(() => import('./components/space-mountain/space-mountain-sso-editor')),
  SpaceMountainUserDeletedAssignmentsPage: lazyDefaultImport(
    () => import('./components/space-mountain/space-mountain-user-deleted-assignments-page'),
  ),
  SpaceStationApiMain: lazyDefaultImport(
    () => import('./components/space-mountain/space-station/space-station-api-main'),
  ),
  SpaceStationRequestsDashboard: lazyDefaultImport(
    () => import('./components/space-mountain/space-station/space-station-requests-dashboard'),
  ),

  SpaceMountainUpsellContentIndex: lazyDefaultImport(
    () => import('./components/space-mountain/upsell-content/upsell-content-index'),
  ),
  SpaceMountainUpsellContentLogin: lazyDefaultImport(
    () => import('./components/space-mountain/upsell-content/upsell-content-login-page'),
  ),

  // Other
  ErrorPage: lazyNamedImport(() => import('./features/common/components/ErrorPage'), 'ErrorPage'),
  DebugPanelWrapper: lazyDefaultImport(() => import('./components/dev-tools/DebugPanelWrapper')),
  ActionMessageContainer: lazyDefaultImport(() => import('./components/widget/action-message-container')),
  WRModalsContainer: lazyDefaultImport(() => import('./components/wr-modals/wr-modals-container')),
};
